import * as React from 'react';
import { cn } from '@/lib/cn';

/**
 * Label, control, hint and error as one block. Every control in the app is labelled.
 *
 * The label wraps the control, which is what names it. The hint and the error sit **outside** the
 * label and are tied to the control by `aria-describedby` instead: inside, they became part of
 * the field's name, so a reader heard "Age, must be 18 or over, required" as what the box was
 * called. The error also marks the control `aria-invalid`, which is what `focusFirstError`
 * looks for. Both are added to a single child control; a field holding several leaves them to it.
 */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
  className?: string;
}) {
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  let control = children;
  if (React.isValidElement<Record<string, unknown>>(children) && describedBy) {
    const own = children.props['aria-describedby'];
    control = React.cloneElement(children, {
      'aria-describedby': [own, describedBy].filter(Boolean).join(' '),
      ...(error ? { 'aria-invalid': true } : {}),
    });
  }

  return (
    <div className={cn('grid gap-2', className)}>
      <label className="grid gap-2">
        <span className="text-sm text-foreground">{label}</span>
        {control}
      </label>
      {hint ? (
        <span id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role="alert" className="ee-error">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function Select({ className, children, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        'min-h-11 w-full rounded-sm bg-input px-3 text-sm text-foreground',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

export function Textarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(
        'min-h-32 w-full rounded-sm bg-input p-3 text-sm text-foreground',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
      {...props}
    />
  );
}
